import React, { useState, useEffect, useRef } from 'react';
import {
  Send,
  Star,
  Clock,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Link2,
  Save,
  Edit3,
  ExternalLink,
  Search,
  X,
  Copy,
  Shield,
  ChevronRight,
  ChevronDown,
  History,
  Inbox,
} from 'lucide-react';
import { supabase, authHeaders } from '../supabaseClient';

/**
 * ReviewsTab — Customer Reviews dashboard
 *
 * Receipts-driven inbox. Surveys auto-send when a delivery is completed
 * (via chat-survey.py). Customers hit /review?r={receipt_id}, rate, and
 * are then shown BOTH options equally — leave private feedback for the
 * merchant, or post a public Google review — regardless of their score.
 * This equal-pathways flow (sometimes called "Option B" or parallel
 * pathways) keeps us aligned with Google's April 2026 review policy and
 * the FTC Consumer Review Rule (16 CFR Part 465), which together
 * prohibit sentiment-based review gating.
 *
 * The merchant's job here is triage — read incoming feedback,
 * address low ratings, watch the trend. Internal categorization of
 * ratings (e.g. "needs response" for low scores) is fine — what's not
 * allowed is selectively soliciting reviews based on sentiment.
 *
 * Manual send: merchants can manually send (or resend) a survey to any
 * recent customer via the "Send survey" card → drawer. Per-receipt
 * resend also exists inside the receipt detail modal
 * (SurveyFeedbackDisplay.jsx) for in-context use.
 */

const BRAND_BLUE = '#2563eb';

// ==========================================
// HELPERS
// ==========================================

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  if (diffHr < 24) return `${diffHr}h`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatPhoneDisplay = (e164) => {
  if (!e164) return '';
  const digits = String(e164).replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (ten.length === 10) return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
  return e164;
};

const isLowRating = (s) =>
  s.customer_satisfaction_score != null && s.customer_satisfaction_score <= 3;

// ---- Low-rating alert acknowledgment ----
// The red "new low ratings" alert behaves like a notification, not a
// permanent banner: it appears when low ratings arrive that the merchant
// hasn't looked at yet, and it retires once they tap through to review
// them. "Seen" receipt ids persist per store so the alert doesn't come
// back on the next page load — only a NEW low rating brings it back.
// Swap these two functions for a Supabase-backed version later if the
// dismissal needs to sync across devices/staff.
const ackStorageKey = (storeId) => `exitium.reviews.lowRatingsSeen.${storeId}`;

const loadAckedIds = (storeId) => {
  try {
    const raw = window.localStorage.getItem(ackStorageKey(storeId));
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
};

const saveAckedIds = (storeId, ids) => {
  try {
    window.localStorage.setItem(ackStorageKey(storeId), JSON.stringify([...ids]));
  } catch {
    // Private mode / storage quota — alert falls back to session-only dismissal.
  }
};

const AVATAR_PALETTE = [
  { bg: 'bg-slate-100', text: 'text-slate-600' },
  { bg: 'bg-blue-50', text: 'text-blue-700' },
  { bg: 'bg-amber-50', text: 'text-amber-700' },
  { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  { bg: 'bg-violet-50', text: 'text-violet-700' },
  { bg: 'bg-rose-50', text: 'text-rose-700' },
  { bg: 'bg-cyan-50', text: 'text-cyan-700' },
  { bg: 'bg-indigo-50', text: 'text-indigo-700' },
];
const hashAvatar = (key = '') => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
};
const getInitials = (name = '') => {
  const trimmed = (name || '').trim();
  if (!trimmed) return '·';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return (parts[0][0] || '·').toUpperCase();
  return ((parts[0][0] || '') + (parts[parts.length - 1][0] || '')).toUpperCase();
};

// ==========================================
// PRIMITIVES
// ==========================================

const Avatar = ({ name, phone, size = 'md' }) => {
  const palette = hashAvatar(name || phone || '');
  const initials = getInitials(name);
  const sizes = {
    sm: 'w-7 h-7 text-[11px]',
    md: 'w-9 h-9 text-xs',
    lg: 'w-10 h-10 text-sm',
  };
  return (
    <div className={`${sizes[size]} rounded-full ${palette.bg} ${palette.text} flex items-center justify-center font-semibold flex-shrink-0`}>
      {initials}
    </div>
  );
};

const StarRating = ({ score = 0, size = 12 }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${score} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        style={{ width: size, height: size }}
        className={n <= score ? 'fill-amber-400 text-amber-400' : 'fill-slate-200 text-slate-200'}
      />
    ))}
  </span>
);

const Metric = ({ label, value, sublabel, alert = false, accent = false }) => (
  <div
    className={`rounded-xl px-4 py-3.5 transition-all ${
      accent && alert
        ? 'bg-red-50 border border-red-200 ring-1 ring-red-200/60'
        : 'bg-white border border-slate-200'
    }`}
  >
    <div className="flex items-center gap-1.5 mb-1.5">
      <span className={`text-[11px] font-medium ${accent && alert ? 'text-red-700' : 'text-slate-500'}`}>
        {label}
      </span>
      {alert && <span className="w-1.5 h-1.5 rounded-full bg-red-500" />}
    </div>
    <p
      className={`text-[24px] font-semibold leading-none tabular-nums tracking-tight ${
        accent && alert ? 'text-red-600' : 'text-slate-900'
      }`}
    >
      {value}
    </p>
    {sublabel && (
      <p className={`text-[11px] mt-1.5 ${accent && alert ? 'text-red-600/80' : 'text-slate-400'}`}>
        {sublabel}
      </p>
    )}
  </div>
);

const ActivityRow = ({ event, onClick }) => {
  const isLow = event.type === 'low_rating';
  const isPositive = event.type === 'positive';
  const isPending = event.type === 'pending';
  const Wrapper = onClick ? 'button' : 'div';

  // Pending rows render as a single compact line — they're not actionable,
  // they just confirm the survey was sent. No need to take 3 lines of space.
  if (isPending) {
    return (
      <Wrapper
        onClick={onClick}
        className={`w-full flex items-center gap-2.5 px-4 py-2 text-left transition-colors hover:bg-slate-50 ${
          onClick ? 'cursor-pointer' : 'cursor-default'
        }`}
      >
        <Avatar name={event.name} phone={event.phone} size="sm" />
        <span className="text-[13px] text-slate-700 truncate min-w-0">
          {event.name || 'Anonymous'}
        </span>
        {event.phone && (
          <span className="text-[11px] text-slate-400 font-mono truncate">
            {formatPhoneDisplay(event.phone)}
          </span>
        )}
        <span className="ml-auto flex items-center gap-2 flex-shrink-0">
          <span className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">
            Survey sent
          </span>
          <span className="text-[11px] text-slate-400 tabular-nums">
            {formatDate(event.timestamp)}
          </span>
        </span>
      </Wrapper>
    );
  }

  // Reviewed rows (low / positive) get a 2-line treatment with feedback excerpt.
  return (
    <Wrapper
      onClick={onClick}
      className={`relative w-full flex items-start gap-2.5 px-4 py-2.5 text-left transition-colors ${
        isLow ? 'bg-red-50/40 hover:bg-red-50/70' : 'hover:bg-slate-50'
      } ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
    >
      {isLow && <span className="absolute left-0 top-2 bottom-2 w-0.5 bg-red-500 rounded-r" />}

      <Avatar name={event.name} phone={event.phone} size="sm" />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] font-medium text-slate-900 truncate">
            {event.name || 'Anonymous'}
          </span>
          <StarRating score={event.score} size={11} />
          {isLow && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold uppercase tracking-wide">
              Needs response
            </span>
          )}
          {isPositive && event.googleRequested && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold uppercase tracking-wide inline-flex items-center gap-1">
              <CheckCircle className="w-2.5 h-2.5" /> On Google
            </span>
          )}
          <span className="text-[11px] text-slate-400 ml-auto flex-shrink-0 tabular-nums">
            {formatDate(event.timestamp)}
          </span>
        </div>

        {event.feedback ? (
          <p className="text-[12px] text-slate-600 leading-snug line-clamp-1 mt-0.5">
            &ldquo;{event.feedback}&rdquo;
          </p>
        ) : (
          <p className="text-[12px] text-slate-400 leading-snug mt-0.5">
            {formatPhoneDisplay(event.phone) || 'No comment'}
          </p>
        )}
      </div>

      {onClick && <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0 mt-0.5" />}
    </Wrapper>
  );
};

const Drawer = ({ open, onClose, title, subtitle, icon: Icon, iconTone = 'blue', children, width = 'md', footer = null }) => {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const widths = { sm: 'sm:max-w-sm', md: 'sm:max-w-md', lg: 'sm:max-w-lg', xl: 'sm:max-w-xl', '2xl': 'sm:max-w-2xl' };
  const tones = {
    blue:    'bg-blue-50 border-blue-200 text-blue-600',
    amber:   'bg-amber-50 border-amber-200 text-amber-600',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-600',
    slate:   'bg-slate-100 border-slate-200 text-slate-600',
    red:     'bg-red-50 border-red-200 text-red-600',
  };

  return (
    <div className={`fixed inset-0 z-50 transition-opacity duration-300 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} aria-hidden={!open}>
      <div onClick={onClose} className={`absolute inset-0 bg-slate-900/40 backdrop-blur-[2px] transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0'}`} />
      <div className={`absolute right-0 top-0 bottom-0 w-full ${widths[width] || widths.md} bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex-shrink-0 flex items-start justify-between gap-3 px-5 pb-4 pt-[max(16px,env(safe-area-inset-top))] border-b border-slate-200">
          <div className="flex items-center gap-3 min-w-0">
            {Icon && (
              <div className={`w-10 h-10 rounded-xl border flex items-center justify-center flex-shrink-0 ${tones[iconTone]}`}>
                <Icon className="w-5 h-5" />
              </div>
            )}
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-slate-900 truncate">{title}</h3>
              {subtitle && <p className="text-xs text-slate-500 truncate">{subtitle}</p>}
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-all flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className={`flex-1 overflow-y-auto p-5 ${footer ? '' : 'pb-[max(20px,env(safe-area-inset-bottom))]'}`}>{children}</div>
        {footer && (
          <div className="flex-shrink-0 px-5 pt-3.5 pb-[max(14px,env(safe-area-inset-bottom))] border-t border-slate-200 bg-slate-50/60">{footer}</div>
        )}
      </div>
    </div>
  );
};

const getSurveyStatusBadge = (survey) => {
  if (survey.customer_satisfaction_score) {
    const score = survey.customer_satisfaction_score;
    return (
      <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold ${
        score >= 4 ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'
      }`}>
        <Star className="w-2.5 h-2.5 fill-current" /><span>{score}/5</span>
      </div>
    );
  }
  if (survey.survey_status === 'in_progress' || survey.survey_status === 'sent') {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-amber-50 border border-amber-200 text-amber-700">
        <Clock className="w-2.5 h-2.5" /><span>Pending</span>
      </div>
    );
  }
  return (
    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-slate-100 border border-slate-200 text-slate-600">
      <Send className="w-2.5 h-2.5" /><span>Sent</span>
    </div>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================
const ReviewsTab = ({ storeId, store, addNotification, hideHeader = false }) => {
  const [loading, setLoading] = useState(true);

  const [drawerView, setDrawerView] = useState(null);
  const [drawerData, setDrawerData] = useState(null);
  const openDrawer = (view, data = null) => { setDrawerView(view); setDrawerData(data); };
  const closeDrawer = () => { setDrawerView(null); setDrawerData(null); };

  const [messageTemplate, setMessageTemplate] = useState('');
  const [googleReviewUrl, setGoogleReviewUrl] = useState('');
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [templateSaved, setTemplateSaved] = useState(false);

  // ---- Manual send state ----
  // Simple phone-input send: merchant types a customer's phone, we look up
  // their most recent receipt and fire the same /api/chat-survey endpoint
  // that the automated and in-context paths use, so the SMS body and
  // landing-page flow are identical regardless of entry point.
  const [manualPhone, setManualPhone] = useState('');
  const [manualSending, setManualSending] = useState(false);
  const [manualResult, setManualResult] = useState(null); // 'success' | 'error' | null

  // ---- Customer typeahead state ----
  // The manual send input doubles as a customer search: typing letters
  // searches receipts by customer_name, typing digits searches by
  // customer_phone (substring match against the stored E.164 value).
  // Picking a result locks in that customer (selectedCustomer) so the
  // send can link directly to their most recent receipt without a second
  // lookup. Free-form phone entry still works for customers with no
  // receipt in the system.
  const [customerResults, setCustomerResults] = useState([]);
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const searchTicketRef = useRef(0); // guards against stale async results
  const searchBoxRef = useRef(null); // for click-outside close

  const [sentSurveys, setSentSurveys] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historySearchQuery, setHistorySearchQuery] = useState('');

  const [activityFilter, setActivityFilter] = useState('all');
  const [filterDefaulted, setFilterDefaulted] = useState(false);
  const [activityCollapsed, setActivityCollapsed] = useState(false);

  // ---- Low-rating alert (notification-style) ----
  // ackedLowIds: receipt ids whose low rating the merchant already reviewed.
  // The alert only counts ratings NOT in this set, so it clears on tap and
  // only returns when a new low rating arrives.
  const [ackedLowIds, setAckedLowIds] = useState(() => loadAckedIds(storeId));
  const [lowAlertLeaving, setLowAlertLeaving] = useState(false); // exit animation in flight
  const [lowAlertIn, setLowAlertIn] = useState(false); // enter animation state
  const lowAlertTimerRef = useRef(null);
  const activitySectionRef = useRef(null);

  useEffect(() => {
    setAckedLowIds(loadAckedIds(storeId));
    setLowAlertLeaving(false);
    setLowAlertIn(false);
  }, [storeId]);

  useEffect(() => () => clearTimeout(lowAlertTimerRef.current), []);

  // Drive the alert's enter transition: it mounts collapsed, then expands a
  // frame later so max-height/opacity actually animate instead of snapping.
  useEffect(() => {
    const shouldShow = sentSurveys.some((s) => isLowRating(s) && !ackedLowIds.has(s.id));
    if (shouldShow && !lowAlertLeaving) {
      const raf = requestAnimationFrame(() => setLowAlertIn(true));
      return () => cancelAnimationFrame(raf);
    }
    if (!shouldShow && !lowAlertLeaving) setLowAlertIn(false);
    return undefined;
  }, [sentSurveys, ackedLowIds, lowAlertLeaving]);

  // After data loads, if there are low ratings the merchant hasn't seen yet,
  // auto-focus the "Needs response" tab. Only happens once per mount so we
  // don't fight the user if they intentionally switch filters, and only for
  // UNSEEN ratings so the page doesn't reopen on red for old, handled ones.
  useEffect(() => {
    if (loading || filterDefaulted) return;
    const hasNew = sentSurveys.some((s) => isLowRating(s) && !ackedLowIds.has(s.id));
    if (hasNew) setActivityFilter('needs_response');
    setFilterDefaulted(true);
  }, [loading, filterDefaulted, sentSurveys, ackedLowIds]);

  // ==========================================
  // DATA LOADING
  // ==========================================
  useEffect(() => {
    if (storeId) loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadStoreSettings(), loadSentHistory()]);
    } catch (error) {
      console.error('Error loading reviews data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStoreSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('store_settings')
        .select('*')
        .eq('store_id', storeId)
        .single();
      if (error && error.code !== 'PGRST116') return;
      if (data) {
        setGoogleReviewUrl(data.google_review_url || '');
        setMessageTemplate(data.campaign_sms_template || getDefaultTemplate(data.display_name || store?.name));
      } else {
        setMessageTemplate(getDefaultTemplate(store?.name));
      }
    } catch (err) {
      console.error('Error loading store settings:', err);
    }
  };

  const getDefaultTemplate = (storeName) =>
    `Hi! Thank you for choosing ${storeName || 'our business'}. We'd love to hear about your experience! Please tap the link below to leave a quick rating:`;

  // FIXED: removed nonexistent receipts.survey_feedback column;
  // fetch from customer_feedback and merge client-side.
  const loadSentHistory = async () => {
    try {
      setLoadingHistory(true);

      const { data: surveyReceipts, error } = await supabase
        .from('receipts')
        .select(
          'id, customer_name, customer_phone, customer_satisfaction_score, ' +
          'survey_status, survey_completed_at, review_sms_sent_at, ' +
          'google_review_requested, created_at'
        )
        .eq('store_id', storeId)
        .not('survey_status', 'is', null)
        .order('review_sms_sent_at', { ascending: false, nullsFirst: false })
        .limit(50);

      if (error) {
        console.error('receipts query failed:', error);
        return;
      }

      if (!surveyReceipts || surveyReceipts.length === 0) {
        setSentSurveys([]);
        return;
      }

      const receiptIds = surveyReceipts.map((r) => r.id);
      const { data: feedbackRows, error: feedbackErr } = await supabase
        .from('customer_feedback')
        .select('receipt_id, feedback, rating, category, created_at')
        .in('receipt_id', receiptIds);

      if (feedbackErr) {
        console.error('customer_feedback query failed:', feedbackErr);
      }

      const feedbackByReceipt = {};
      (feedbackRows || []).forEach((row) => {
        const existing = feedbackByReceipt[row.receipt_id];
        if (!existing || new Date(row.created_at) > new Date(existing.created_at)) {
          feedbackByReceipt[row.receipt_id] = row;
        }
      });

      const merged = surveyReceipts.map((r) => ({
        ...r,
        survey_feedback: feedbackByReceipt[r.id]?.feedback || null,
      }));
      setSentSurveys(merged);
    } catch (err) {
      console.error('Error loading history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // ==========================================
  // TEMPLATE
  // ==========================================
  const handleSaveTemplate = async () => {
    if (!messageTemplate.trim()) return;
    setIsSavingTemplate(true);
    try {
      const { error } = await supabase
        .from('store_settings')
        .upsert({ store_id: storeId, campaign_sms_template: messageTemplate.trim() }, { onConflict: 'store_id' });
      if (error) throw error;
      setTemplateSaved(true);
      setTimeout(() => setTemplateSaved(false), 3000);
      if (addNotification) addNotification('Message template saved!', 'success');
      setTimeout(() => closeDrawer(), 600);
    } catch (err) {
      console.error('Error saving template:', err);
      if (addNotification) addNotification('Failed to save template', 'error');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  // ==========================================
  // MANUAL SEND
  // ==========================================

  // Debounced customer search against receipts. Letters → name search,
  // digits → phone search. Results are deduped by phone, keeping each
  // customer's most recent receipt so selection links to the latest
  // transaction. A ticket counter discards out-of-order responses.
  useEffect(() => {
    // A selected customer means the field holds a chosen value, not a query.
    if (selectedCustomer) {
      setCustomerResults([]);
      setCustomerSearchOpen(false);
      return;
    }

    const raw = manualPhone.trim();
    const digits = raw.replace(/\D/g, '');
    const isNameQuery = /[a-zA-Z]/.test(raw);

    // Minimum query length: 2 chars for names, 3 digits for phones —
    // anything shorter matches too much to be useful.
    if ((isNameQuery && raw.length < 2) || (!isNameQuery && digits.length < 3)) {
      setCustomerResults([]);
      setCustomerSearchOpen(false);
      setHighlightIndex(-1);
      return;
    }

    const ticket = ++searchTicketRef.current;
    const timer = setTimeout(async () => {
      setCustomerSearching(true);
      try {
        // Strip characters that have meaning in PostgREST ilike patterns
        // or filter syntax so user input can't break the query.
        const safe = raw.replace(/[%_,()\\]/g, ' ').replace(/\s+/g, ' ').trim();

        let query = supabase
          .from('receipts')
          .select('id, customer_name, customer_phone, created_at')
          .eq('store_id', storeId)
          .not('customer_phone', 'is', null)
          .order('created_at', { ascending: false })
          .limit(30);

        query = isNameQuery
          ? query.ilike('customer_name', `%${safe}%`)
          : query.ilike('customer_phone', `%${digits}%`);

        const { data, error } = await query;
        if (error) throw error;
        if (ticket !== searchTicketRef.current) return; // stale response

        // Dedupe by phone — rows are already newest-first, so the first
        // receipt we see per phone is that customer's most recent.
        const byPhone = new Map();
        (data || []).forEach((r) => {
          if (!byPhone.has(r.customer_phone)) byPhone.set(r.customer_phone, r);
        });
        const results = Array.from(byPhone.values()).slice(0, 8);

        setCustomerResults(results);
        setCustomerSearchOpen(results.length > 0);
        setHighlightIndex(results.length > 0 ? 0 : -1);
      } catch (err) {
        console.warn('Customer search failed:', err);
      } finally {
        if (ticket === searchTicketRef.current) setCustomerSearching(false);
      }
    }, 250);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualPhone, selectedCustomer, storeId]);

  // Close the results dropdown when clicking anywhere outside the combobox.
  useEffect(() => {
    const handler = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
        setCustomerSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectCustomer = (c) => {
    setSelectedCustomer(c);
    setManualPhone(formatPhoneDisplay(c.customer_phone));
    setCustomerResults([]);
    setCustomerSearchOpen(false);
    setHighlightIndex(-1);
  };

  const clearSelectedCustomer = () => {
    setSelectedCustomer(null);
    setManualPhone('');
  };

  // Normalize a user-entered phone string to E.164. We strip everything that
  // isn't a digit. If we end up with 10 digits, prepend +1; if 11 starting
  // with 1, prepend +; otherwise prepend + and use as-is. This handles the
  // common US formats: "(415) 555-1234", "415-555-1234", "4155551234",
  // "+14155551234", and "14155551234".
  const normalizePhone = (raw) => {
    if (!raw) return '';
    const digits = String(raw).replace(/\D/g, '');
    if (!digits) return '';
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return `+${digits}`;
  };

  // Send a survey by phone number. Tries to find an existing receipt for
  // this phone (so the survey ties to a transaction if there is one), but
  // if no receipt exists we still send by phone — important for
  // non-furniture verticals like dental or salon where every customer
  // doesn't necessarily have a receipt in the system. The backend
  // (/api/chat-survey) is expected to handle either form.
  const handleManualSend = async () => {
    if (manualSending) return;

    // A selected customer carries an authoritative E.164 phone and receipt
    // id. Otherwise normalize whatever was typed — if that yields nothing
    // (e.g. a name typed with no result picked), tell the user instead of
    // firing a junk request.
    const phone = selectedCustomer
      ? selectedCustomer.customer_phone
      : normalizePhone(manualPhone);

    if (!phone || phone.replace(/\D/g, '').length < 10) {
      if (addNotification) {
        addNotification('Pick a customer from the list or enter a full phone number', 'error');
      }
      return;
    }

    setManualSending(true);
    setManualResult(null);

    try {
      let receiptId = selectedCustomer?.id || null;
      let displayName = selectedCustomer?.customer_name || formatPhoneDisplay(phone);

      // No selection → try to find the most recent receipt for this phone
      // so the survey ties to a transaction if there is one. Lookup failure
      // is not fatal — we still send by phone.
      if (!receiptId) {
        try {
          const { data: receipts } = await supabase
            .from('receipts')
            .select('id, customer_name, customer_phone')
            .eq('store_id', storeId)
            .eq('customer_phone', phone)
            .order('created_at', { ascending: false })
            .limit(1);

          if (receipts && receipts.length > 0) {
            receiptId = receipts[0].id;
            if (receipts[0].customer_name) displayName = receipts[0].customer_name;
          }
        } catch (lookupErr) {
          console.warn('Receipt lookup failed, sending by phone:', lookupErr);
        }
      }

      // Build payload: prefer receipt_id when we have one, otherwise send
      // store_id + customer_phone so the backend can either find/create a
      // placeholder record or trigger a no-receipt survey flow.
      const payload = {
        conversation_stage: 'start_survey',
        // Manual sends/resends are intentional — bypass the duplicate-survey
        // guard in chat-survey.py (automated triggers never set this flag)
        force: true,
        ...(receiptId
          ? { receipt_id: receiptId }
          : { store_id: storeId, customer_phone: phone }),
      };

      const resp = await fetch('/api/chat-survey', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await resp.json();

      if (data.success) {
        setManualResult('success');
        setManualPhone('');
        setSelectedCustomer(null);
        if (addNotification) addNotification(`Survey sent to ${displayName}`, 'success');
        // Refresh history so the recipient shows up in the inbox immediately
        loadSentHistory();
        setTimeout(() => setManualResult(null), 4000);
      } else {
        console.error('Manual send failed:', data.error);
        setManualResult('error');
        if (addNotification) addNotification(data.error || 'Failed to send survey', 'error');
        setTimeout(() => setManualResult(null), 4000);
      }
    } catch (err) {
      console.error('Manual send error:', err);
      setManualResult('error');
      if (addNotification) addNotification('Network error sending survey', 'error');
      setTimeout(() => setManualResult(null), 4000);
    } finally {
      setManualSending(false);
    }
  };

  // ==========================================
  // DERIVED
  // ==========================================
  const completedSurveys = sentSurveys.filter((s) => s.customer_satisfaction_score != null);
  const avgRating =
    completedSurveys.length > 0
      ? (completedSurveys.reduce((sum, s) => sum + s.customer_satisfaction_score, 0) / completedSurveys.length).toFixed(1)
      : '—';
  const lowRatingSurveys = completedSurveys.filter((s) => s.customer_satisfaction_score <= 3);
  const lowRatingCount = lowRatingSurveys.length; // total — powers the "Needs attention" metric
  const newLowRatingCount = lowRatingSurveys.filter((s) => !ackedLowIds.has(s.id)).length; // unseen — powers the alert
  const showLowAlert = newLowRatingCount > 0 || lowAlertLeaving; // stays mounted through the exit animation
  const lowAlertExpanded = lowAlertIn && !lowAlertLeaving;
  const reviewRequestsSentCount = sentSurveys.filter((s) => s.google_review_requested).length;
  const completionRate =
    sentSurveys.length > 0 ? Math.round((completedSurveys.length / sentSurveys.length) * 100) : 0;
  const googleConversionRate =
    completedSurveys.length > 0 ? Math.round((reviewRequestsSentCount / completedSurveys.length) * 100) : 0;

  const activityFeed = [
    ...completedSurveys.map((s) => ({
      id: `survey-${s.id}`,
      type: s.customer_satisfaction_score <= 3 ? 'low_rating' : 'positive',
      name: s.customer_name || '',
      phone: s.customer_phone,
      score: s.customer_satisfaction_score,
      feedback: s.survey_feedback,
      googleRequested: !!s.google_review_requested,
      timestamp: s.survey_completed_at || s.review_sms_sent_at || s.created_at,
      raw: s,
    })),
    ...sentSurveys
      .filter((s) => s.customer_satisfaction_score == null && s.review_sms_sent_at)
      .map((s) => ({
        id: `pending-${s.id}`,
        type: 'pending',
        name: s.customer_name || '',
        phone: s.customer_phone,
        timestamp: s.review_sms_sent_at || s.created_at,
        raw: s,
      })),
  ].sort((a, b) => {
    // Priority: low_rating (actionable) > positive (good news) > pending (just queued).
    // Within the same priority bucket, newest first.
    const priority = { low_rating: 0, positive: 1, pending: 2 };
    const pa = priority[a.type] ?? 99;
    const pb = priority[b.type] ?? 99;
    if (pa !== pb) return pa - pb;
    return new Date(b.timestamp) - new Date(a.timestamp);
  });

  const filteredActivity = (() => {
    switch (activityFilter) {
      case 'needs_response': return activityFeed.filter((e) => e.type === 'low_rating');
      case 'five_star':       return activityFeed.filter((e) => e.type === 'positive' && e.score === 5);
      case 'pending':         return activityFeed.filter((e) => e.type === 'pending');
      default:                return activityFeed;
    }
  })();

  const filterCounts = {
    all: activityFeed.length,
    needs_response: activityFeed.filter((e) => e.type === 'low_rating').length,
    five_star: activityFeed.filter((e) => e.type === 'positive' && e.score === 5).length,
    pending: activityFeed.filter((e) => e.type === 'pending').length,
  };

  const filteredHistory = historySearchQuery
    ? sentSurveys.filter(
        (item) =>
          (item.customer_name || '').toLowerCase().includes(historySearchQuery.toLowerCase()) ||
          (item.customer_phone || '').includes(historySearchQuery.replace(/\D/g, ''))
      )
    : sentSurveys;

  // Tap on the low-rating alert: take the merchant to the filtered list
  // first, then retire the alert. Seen ids persist per store, so it stays
  // gone until a NEW low rating shows up. The persisted set is rebuilt from
  // the current data window each time, so it self-prunes as receipts age out.
  const handleReviewLowRatings = () => {
    if (lowAlertLeaving) return;
    setActivityFilter('needs_response');
    setActivityCollapsed(false);
    requestAnimationFrame(() => {
      activitySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    setLowAlertLeaving(true);
    const seen = new Set(lowRatingSurveys.map((s) => s.id));
    lowAlertTimerRef.current = setTimeout(() => {
      setAckedLowIds(seen);
      saveAckedIds(storeId, seen);
      setLowAlertLeaving(false);
    }, 340); // just past the 300ms exit transition
  };

  // ==========================================
  // LOADING
  // ==========================================
  if (loading) {
    return (
      <div className="flex items-center justify-center p-16 bg-slate-50 h-full">
        <div className="flex flex-col items-center gap-3 text-slate-400">
          <RefreshCw className="w-6 h-6 animate-spin" />
          <span className="text-sm font-medium">Loading…</span>
        </div>
      </div>
    );
  }

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <div
      className={hideHeader
        ? "h-full flex flex-col overflow-hidden bg-slate-50"
        : "h-full flex flex-col overflow-hidden rounded-2xl border border-slate-200/80 shadow-[0_1px_3px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.12)] bg-white"}
      style={{ maxHeight: 'calc(100vh - 4rem)' }}
    >
      {!hideHeader && (
        <div className="flex-shrink-0 bg-gradient-to-b from-white to-slate-50/80 backdrop-blur-xl p-4 border-b border-slate-200 sticky top-0 z-10 shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                Customer <span className="text-blue-600">Reviews</span>
              </h2>
              <p className="text-slate-500 text-xs mt-0.5">
                {sentSurveys.length > 0
                  ? `${completedSurveys.length} of ${sentSurveys.length} ${sentSurveys.length === 1 ? 'survey' : 'surveys'} completed`
                  : 'Surveys send automatically when an order is delivered'}
              </p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => openDrawer('history')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-slate-50 text-slate-700 text-sm font-medium transition-colors"
              >
                <History className="w-3.5 h-3.5 text-slate-400" />
                <span className="hidden sm:inline">History</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={hideHeader ? "flex-1 overflow-y-auto" : "flex-1 overflow-y-auto bg-slate-100/70 shadow-[inset_0_2px_4px_-2px_rgba(15,23,42,0.06)]"}>
        <div className="px-4 py-6 space-y-5">

          {/* Manual send bar — top of page so it's discoverable as an action */}
          <div className="bg-white border border-slate-200 rounded-2xl p-3 sm:p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)]">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
                  <Send className="w-4 h-4" style={{ color: BRAND_BLUE }} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 leading-tight">Send a survey</p>
                  <p className="text-[11px] text-slate-500 leading-tight">Search a customer or enter a phone</p>
                </div>
              </div>
              <div className="flex gap-2 flex-1 min-w-0 w-full sm:w-auto">
                <div ref={searchBoxRef} className="relative flex-1 min-w-0">
                  {customerSearching ? (
                    <RefreshCw className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 animate-spin pointer-events-none" />
                  ) : selectedCustomer ? (
                    <CheckCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-emerald-500 pointer-events-none" />
                  ) : (
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                  )}
                  <input
                    type="text"
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={customerSearchOpen}
                    aria-autocomplete="list"
                    value={manualPhone}
                    onChange={(e) => {
                      setManualPhone(e.target.value);
                      // Any edit invalidates a previous selection — the
                      // field is a query again.
                      if (selectedCustomer) setSelectedCustomer(null);
                    }}
                    onFocus={() => {
                      if (!selectedCustomer && customerResults.length > 0) setCustomerSearchOpen(true);
                    }}
                    onKeyDown={(e) => {
                      if (customerSearchOpen && customerResults.length > 0) {
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          setHighlightIndex((i) => (i + 1) % customerResults.length);
                          return;
                        }
                        if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          setHighlightIndex((i) => (i - 1 + customerResults.length) % customerResults.length);
                          return;
                        }
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          selectCustomer(customerResults[highlightIndex >= 0 ? highlightIndex : 0]);
                          return;
                        }
                        if (e.key === 'Escape') {
                          setCustomerSearchOpen(false);
                          return;
                        }
                      }
                      if (e.key === 'Enter' && manualPhone.trim() && !manualSending) {
                        handleManualSend();
                      }
                    }}
                    placeholder="Search name or phone…"
                    disabled={manualSending}
                    className={`w-full min-w-0 pl-9 ${selectedCustomer ? 'pr-8' : 'pr-3'} py-2 rounded-lg bg-white border text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/5 transition-all disabled:opacity-60 ${
                      selectedCustomer ? 'border-emerald-300' : 'border-slate-200 focus:border-slate-400'
                    }`}
                  />
                  {selectedCustomer && !manualSending && (
                    <button
                      type="button"
                      onClick={clearSelectedCustomer}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                      aria-label="Clear selected customer"
                    >
                      <X className="w-3 h-3 text-slate-500" />
                    </button>
                  )}

                  {/* Typeahead results */}
                  {customerSearchOpen && customerResults.length > 0 && (
                    <div
                      role="listbox"
                      className="absolute left-0 right-0 top-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-[0_8px_24px_-8px_rgba(15,23,42,0.18)] z-30 overflow-hidden"
                    >
                      <div className="max-h-64 overflow-y-auto divide-y divide-slate-50">
                        {customerResults.map((c, i) => (
                          <button
                            key={c.customer_phone}
                            type="button"
                            role="option"
                            aria-selected={i === highlightIndex}
                            // onMouseDown + preventDefault so the input doesn't
                            // blur (and the dropdown close) before the click lands.
                            onMouseDown={(e) => {
                              e.preventDefault();
                              selectCustomer(c);
                            }}
                            onMouseEnter={() => setHighlightIndex(i)}
                            className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors ${
                              i === highlightIndex ? 'bg-blue-50/70' : 'bg-white'
                            }`}
                          >
                            <Avatar name={c.customer_name} phone={c.customer_phone} size="sm" />
                            <div className="flex-1 min-w-0">
                              <p className="text-[13px] font-medium text-slate-900 truncate">
                                {c.customer_name || 'Unknown'}
                              </p>
                              <p className="text-[11px] text-slate-500 font-mono">
                                {formatPhoneDisplay(c.customer_phone)}
                              </p>
                            </div>
                            <span className="text-[11px] text-slate-400 tabular-nums flex-shrink-0">
                              {formatDate(c.created_at)}
                            </span>
                          </button>
                        ))}
                      </div>
                      <div className="px-3 py-1.5 bg-slate-50 border-t border-slate-100">
                        <p className="text-[10px] text-slate-400">
                          ↑↓ to navigate · Enter to select · Esc to dismiss
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={handleManualSend}
                  disabled={!manualPhone.trim() || manualSending}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ backgroundColor: BRAND_BLUE }}
                  onMouseEnter={(e) => {
                    if (manualPhone.trim() && !manualSending) e.currentTarget.style.backgroundColor = '#1d4ed8';
                  }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = BRAND_BLUE; }}
                >
                  {manualSending ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  ) : manualResult === 'success' ? (
                    <CheckCircle className="w-3.5 h-3.5" />
                  ) : (
                    <Send className="w-3.5 h-3.5" />
                  )}
                  <span>{manualSending ? 'Sending' : manualResult === 'success' ? 'Sent' : 'Send'}</span>
                </button>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 mt-2 flex items-start gap-1.5 leading-relaxed">
              <Shield className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>
                Use manual send for ad-hoc situations (follow-up, walk-in customer,
                missed automation). Only send to customers you have permission to
                text, and follow Google and FTC review guidelines.
              </span>
            </p>
            {manualResult === 'error' && (
              <p className="text-[11px] text-red-600 mt-2 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Send failed — try again
              </p>
            )}
          </div>

          {showLowAlert && (
            <div
              className="overflow-hidden"
              aria-hidden={lowAlertLeaving || undefined}
              style={{
                maxHeight: lowAlertExpanded ? '200px' : '0px',
                opacity: lowAlertExpanded ? 1 : 0,
                transform: lowAlertExpanded ? 'translateY(0)' : 'translateY(-6px)',
                marginTop: lowAlertExpanded ? '1.25rem' : '0rem',
                transition:
                  'max-height 300ms cubic-bezier(0.4,0,0.2,1), opacity 220ms ease, ' +
                  'transform 300ms cubic-bezier(0.4,0,0.2,1), margin-top 300ms cubic-bezier(0.4,0,0.2,1)',
                pointerEvents: lowAlertLeaving ? 'none' : 'auto',
              }}
            >
              <button
                type="button"
                onClick={handleReviewLowRatings}
                className="w-full bg-white border border-red-200/80 rounded-2xl p-4 flex items-center gap-3 text-left transition-colors hover:bg-red-50/50 hover:border-red-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/25 group"
              >
                <div className="relative flex-shrink-0">
                  <div className="w-9 h-9 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                  </div>
                  <span className="absolute -top-0.5 -right-0.5 flex w-2.5 h-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-60" />
                    <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white" />
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">
                    {newLowRatingCount} new low {newLowRatingCount === 1 ? 'rating' : 'ratings'}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                    Reach out privately before they post publicly.
                  </p>
                </div>
                <span className="flex-shrink-0 inline-flex items-center gap-1 pl-3 pr-2 py-1.5 rounded-lg bg-red-600 group-hover:bg-red-700 text-white text-xs font-semibold transition-colors">
                  Review
                  <ChevronRight className="w-3.5 h-3.5" />
                </span>
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Metric
              label="Needs attention"
              value={lowRatingCount}
              sublabel={lowRatingCount > 0 ? 'Low ratings to address' : 'All clear'}
              alert={lowRatingCount > 0}
              accent
            />
            <Metric
              label="Avg rating"
              value={avgRating === '—' ? '—' : avgRating}
              sublabel={completedSurveys.length > 0
                ? `${completedSurveys.length} ${completedSurveys.length === 1 ? 'response' : 'responses'}`
                : 'No responses yet'}
            />
            <Metric
              label="Response rate"
              value={sentSurveys.length > 0 ? `${completionRate}%` : '—'}
              sublabel={sentSurveys.length > 0
                ? `${completedSurveys.length} of ${sentSurveys.length} completed`
                : 'No surveys sent'}
            />
            <Metric
              label="Sent to Google"
              value={reviewRequestsSentCount}
              sublabel={completedSurveys.length > 0 && reviewRequestsSentCount > 0
                ? `${googleConversionRate}% of responses`
                : 'Google links sent'}
            />
          </div>

          <div
            ref={activitySectionRef}
            className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)] scroll-mt-4"
          >
            <div className={`flex items-center justify-between px-4 py-2.5 gap-2 flex-wrap ${activityCollapsed ? '' : 'border-b border-slate-100'}`}>
              <button
                type="button"
                onClick={() => setActivityCollapsed((v) => !v)}
                className="flex items-center gap-2 flex-shrink-0 min-w-0 -ml-1 pl-1 pr-2 py-1 rounded-lg hover:bg-slate-50 transition-colors group"
                aria-expanded={!activityCollapsed}
              >
                <ChevronDown
                  className={`w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-all ${activityCollapsed ? '-rotate-90' : ''}`}
                />
                <Inbox className="w-4 h-4 text-slate-400" />
                <h3 className="text-sm font-semibold text-slate-900">
                  {activityFilter === 'needs_response' ? 'Low ratings to address'
                    : activityFilter === 'five_star' ? '5-star reviews'
                    : activityFilter === 'pending' ? 'Surveys awaiting reply'
                    : 'Recent feedback'}
                </h3>
                {activityCollapsed && filteredActivity.length > 0 && (
                  <span className="text-xs font-medium text-slate-400 tabular-nums">
                    {filteredActivity.length}
                  </span>
                )}
              </button>
              {!activityCollapsed && (
              <div className="flex items-center gap-0.5 flex-wrap">
                {[
                  { id: 'all', label: 'All' },
                  { id: 'needs_response', label: 'Needs response' },
                  { id: 'five_star', label: '5-star' },
                  { id: 'pending', label: 'Awaiting' },
                ].map((tab) => {
                  const count = filterCounts[tab.id];
                  const isActive = activityFilter === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActivityFilter(tab.id)}
                      className="px-2.5 py-1 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5"
                      style={isActive
                        ? { backgroundColor: `${BRAND_BLUE}12`, color: BRAND_BLUE }
                        : { color: '#64748b' }
                      }
                      onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = '#0f172a'; }}
                      onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = '#64748b'; }}
                    >
                      {tab.label}
                      {count > 0 && (
                        <span
                          className="text-[10px] tabular-nums"
                          style={{ color: isActive ? BRAND_BLUE : '#94a3b8', opacity: isActive ? 0.85 : 1 }}
                        >
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              )}
            </div>

            {!activityCollapsed && (
              filteredActivity.length === 0 ? (
              <div className="px-5 py-10 text-center">
                <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center mx-auto mb-2.5">
                  <Inbox className="w-4 h-4 text-slate-400" />
                </div>
                <p className="text-sm font-medium text-slate-700">
                  {activityFilter === 'all' ? 'No feedback yet' : 'Nothing in this view'}
                </p>
                <p className="text-xs text-slate-500 mt-1 max-w-xs mx-auto leading-relaxed">
                  {activityFilter !== 'all'
                    ? 'Try a different filter or wait for new responses.'
                    : 'Responses appear here as customers reply to delivered orders.'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-[360px] overflow-y-auto">
                {filteredActivity.slice(0, 25).map((event) => (
                  <ActivityRow
                    key={event.id}
                    event={event}
                    onClick={
                      event.type === 'low_rating' || event.type === 'positive'
                        ? () => openDrawer('feedbackDetail', event.raw)
                        : null
                    }
                  />
                ))}
              </div>
            )
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={() => openDrawer('editTemplate')}
              className="bg-white border border-slate-200 rounded-2xl p-4 text-left hover:border-slate-300 transition-all group shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)] hover:shadow-[0_4px_12px_-2px_rgba(15,23,42,0.10)] hover:-translate-y-px"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Edit3 className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-xs font-semibold text-slate-900">Message template</span>
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500 transition-colors" />
              </div>
              <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">{messageTemplate}</p>
            </button>

            <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)]">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Link2 className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-xs font-semibold text-slate-900">Google review link</span>
                </div>
                {googleReviewUrl ? (
                  <span className="flex items-center gap-1 text-[11px] font-medium text-emerald-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    Connected
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-[11px] font-medium text-amber-600">
                    <AlertTriangle className="w-3 h-3" />
                    Not set
                  </span>
                )}
              </div>
              {googleReviewUrl ? (
                <a
                  href={googleReviewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-slate-500 hover:text-slate-900 transition-colors inline-flex items-center gap-1 mt-auto self-start"
                >
                  Test link
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <p className="text-xs text-slate-500 mt-auto">
                  Add a Google Review URL in Settings to enable Google routing.
                </p>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* DRAWER: FEEDBACK DETAIL */}
      <Drawer
        open={drawerView === 'feedbackDetail'}
        onClose={closeDrawer}
        title={drawerData?.customer_name || 'Customer Feedback'}
        subtitle={formatPhoneDisplay(drawerData?.customer_phone)}
        icon={Star}
        iconTone={drawerData?.customer_satisfaction_score >= 4 ? 'emerald' : 'red'}
      >
        {drawerData && (
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Rating</p>
              <div className="flex items-center justify-center gap-1 mb-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    className={`w-7 h-7 ${
                      n <= drawerData.customer_satisfaction_score
                        ? 'text-amber-400 fill-amber-400'
                        : 'text-slate-200 fill-slate-200'
                    }`}
                  />
                ))}
              </div>
              <p className="text-sm font-bold text-slate-900">{drawerData.customer_satisfaction_score}/5</p>
            </div>

            {drawerData.survey_feedback && (
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Customer said</p>
                <p className="text-sm text-slate-900 italic leading-relaxed">
                  &ldquo;{drawerData.survey_feedback}&rdquo;
                </p>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-xs text-slate-500">Survey sent</span>
                <span className="text-xs font-semibold text-slate-900">
                  {drawerData.review_sms_sent_at ? new Date(drawerData.review_sms_sent_at).toLocaleString() : '—'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-xs text-slate-500">Completed</span>
                <span className="text-xs font-semibold text-slate-900">
                  {drawerData.survey_completed_at ? new Date(drawerData.survey_completed_at).toLocaleString() : 'Not yet'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-xs text-slate-500">Google review requested</span>
                <span className="text-xs font-semibold text-slate-900">
                  {drawerData.google_review_requested ? '✓ Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>
        )}
      </Drawer>

      {/* DRAWER: EDIT TEMPLATE */}
      <Drawer
        open={drawerView === 'editTemplate'}
        onClose={closeDrawer}
        title="Message template"
        subtitle="Sent automatically when an order is delivered"
        icon={Edit3}
        iconTone="blue"
        width="lg"
        footer={
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={() => { loadStoreSettings(); closeDrawer(); }}
              className="px-4 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium text-sm transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveTemplate}
              disabled={isSavingTemplate || !messageTemplate.trim()}
              className="flex-1 px-4 py-2 rounded-lg text-white font-medium text-sm flex items-center justify-center gap-1.5 transition-all disabled:opacity-50"
              style={{ backgroundColor: BRAND_BLUE }}
              onMouseEnter={(e) => {
                if (!isSavingTemplate && messageTemplate.trim()) e.currentTarget.style.backgroundColor = '#1d4ed8';
              }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = BRAND_BLUE; }}
            >
              {isSavingTemplate ? <RefreshCw className="w-4 h-4 animate-spin" /> : templateSaved ? <CheckCircle className="w-4 h-4" /> : <Save className="w-4 h-4" />}
              {templateSaved ? 'Saved!' : 'Save template'}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Your message</label>
            <textarea
              value={messageTemplate}
              onChange={(e) => setMessageTemplate(e.target.value)}
              className="mt-1.5 w-full h-32 p-3.5 rounded-lg bg-white border border-slate-200 text-sm text-slate-900 resize-none focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/5 transition-all"
              placeholder="Enter your custom survey message…"
            />
          </div>

          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Preview</label>
            <div className="mt-1.5 bg-slate-50 border border-slate-200 rounded-xl p-3.5">
              <div className="bg-white rounded-lg border border-slate-200 p-3.5 text-[13px] text-slate-700 leading-relaxed">
                <p>
                  {messageTemplate || <span className="italic text-slate-400">Your message will appear here</span>}
                </p>
                <p className="mt-2 text-blue-600 underline text-xs break-all">
                  https://exitiumlabs.com/review?r=…
                </p>
                <p className="mt-2 text-[11px] text-slate-400 italic">
                  Reply STOP to opt out. Reply HELP for help. Msg & data rates may apply.
                </p>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(messageTemplate);
                  if (addNotification) addNotification('Copied to clipboard', 'success');
                }}
                className="mt-2 flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700 font-semibold transition-colors"
              >
                <Copy className="w-3 h-3" /> Copy preview
              </button>
            </div>
          </div>

          <div className="flex items-start gap-1.5 text-[11px] text-slate-400">
            <Shield className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>
              Review page link and legal footer (STOP/HELP) are appended automatically.
              Every customer is shown the same two options after rating — leave private
              feedback, or post a Google review — regardless of their score. This keeps
              us aligned with Google's April 2026 review policy and the FTC Consumer
              Review Rule.
            </span>
          </div>
        </div>
      </Drawer>
      {/* DRAWER: HISTORY */}
      <Drawer
        open={drawerView === 'history'}
        onClose={closeDrawer}
        title="Survey history"
        subtitle={`${sentSurveys.length} ${sentSurveys.length === 1 ? 'survey' : 'surveys'}`}
        icon={History}
        iconTone="slate"
        width="xl"
      >
        <div className="space-y-3">
          {sentSurveys.length > 5 && (
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={historySearchQuery}
                onChange={(e) => setHistorySearchQuery(e.target.value)}
                placeholder="Search history…"
                className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-900/5 transition-all"
              />
              {historySearchQuery && (
                <button
                  onClick={() => setHistorySearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200"
                >
                  <X className="w-3 h-3 text-slate-500" />
                </button>
              )}
            </div>
          )}

          {loadingHistory ? (
            <div className="p-8 flex items-center justify-center">
              <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-8 text-center">
              <p className="text-xs text-slate-500">
                {historySearchQuery ? `No matches for "${historySearchQuery}"` : 'No surveys yet'}
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {filteredHistory.map((item) => (
                <div
                  key={item.id}
                  className="bg-white border border-slate-200 rounded-xl p-3 hover:border-slate-300 transition-all cursor-pointer"
                  onClick={() => item.customer_satisfaction_score != null && openDrawer('feedbackDetail', item)}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      <Avatar name={item.customer_name} phone={item.customer_phone} size="sm" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-slate-900 truncate">
                            {item.customer_name || 'Unknown'}
                          </p>
                          {item.google_review_requested && (
                            <span className="px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 rounded text-[10px] text-emerald-700 font-bold">
                              GOOGLE ✓
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <p className="text-[11px] text-slate-500 font-mono">
                            {formatPhoneDisplay(item.customer_phone) || '—'}
                          </p>
                          <span className="text-[11px] text-slate-400">·</span>
                          <span className="text-[11px] text-slate-400">
                            {formatDate(item.review_sms_sent_at || item.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0">{getSurveyStatusBadge(item)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Drawer>
    </div>
  );
};

export default ReviewsTab;